import { useCallback, useEffect, useRef, useState } from "react";
import {
  PlanTemplate,
  ProgramModel,
  ProgramTemplate,
  type PlanTemplate as PlanTemplateT,
  type ProgramModel as ProgramModelT,
  type ProgramTemplate as ProgramTemplateT,
} from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";

// Shared JL-official templates fetch — the first shared home for the
// /api/templates data path (lifted from mobile's TemplatesShelf, which
// fetched one kind per instance; the Library browser needs both). Same
// posture as the shelf: defensive per-item parse (drop bad rows, warn),
// fetch once per mount — the browser mounts when the Create-hub opens,
// so this IS the lazy load. Each caller names the kinds it reads, and
// `reload` tries again after an error. apiFetch via ref, same convention as
// usePrograms (a fresh literal per render must not re-kick the fetch).

export type TemplatesStatus = "loading" | "ready" | "error";

const parseItems = <T>(
  data: unknown,
  schema: { safeParse: (raw: unknown) => { success: boolean; data?: T } },
  label: string,
): T[] => {
  const arr =
    data && typeof data === "object" && "items" in data
      ? (data as { items: unknown }).items
      : [];
  const items: T[] = [];
  if (Array.isArray(arr)) {
    for (const raw of arr) {
      const parsed = schema.safeParse(raw);
      if (parsed.success && parsed.data !== undefined) items.push(parsed.data);
      else console.warn(`[templates] ${label}_invalid`);
    }
  }
  return items;
};

/** What a caller reads. The Library reads plans and programs; «New
 *  program» reads programs and the program models (#351). */
export type TemplateReadKind = "plan" | "program" | "model";
const DEFAULT_KINDS: readonly TemplateReadKind[] = ["plan", "program"];
const PATH: Record<TemplateReadKind, string> = {
  plan: "/api/templates/plans",
  program: "/api/templates/programs",
  model: "/api/templates/models",
};

export function useTemplates(opts: {
  apiFetch: ProgramApiFetch;
  kinds?: readonly TemplateReadKind[];
}) {
  const [planTemplates, setPlanTemplates] = useState<PlanTemplateT[]>([]);
  const [programTemplates, setProgramTemplates] = useState<
    ProgramTemplateT[]
  >([]);
  const [models, setModels] = useState<ProgramModelT[]>([]);
  const [status, setStatus] = useState<TemplatesStatus>("loading");
  const [attempt, setAttempt] = useState(0);
  const kindsKey = (opts.kinds ?? DEFAULT_KINDS).join(",");

  const apiRef = useRef(opts.apiFetch);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
  });

  useEffect(() => {
    let cancelled = false;
    const kinds = kindsKey.split(",") as TemplateReadKind[];
    const read = (kind: TemplateReadKind) =>
      kinds.includes(kind)
        ? apiRef.current<unknown>(PATH[kind])
        : Promise.resolve(null);
    setStatus("loading");
    void (async () => {
      try {
        const [plansRaw, programsRaw, modelsRaw] = await Promise.all([
          read("plan"),
          read("program"),
          read("model"),
        ]);
        if (cancelled) return;
        setPlanTemplates(parseItems(plansRaw, PlanTemplate, "plan"));
        setProgramTemplates(
          parseItems(programsRaw, ProgramTemplate, "program"),
        );
        setModels(parseItems(modelsRaw, ProgramModel, "model"));
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kindsKey, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { planTemplates, programTemplates, models, status, reload };
}
